<app-storage-warning></app-storage-warning>

<div
  *ngIf="!isProjectAuthorized && !isViewingDownloadedContent"
  style="display: flex; justify-content: center; align-items: center; min-height: 60vh"
>
  <mat-card class="project-warning">
    <div class="warning" style="display: flex; align-items: center">
      <mat-icon color="warn" aria-label="Warning" style="margin-right: 8px">info</mat-icon>
      <strong>Permission denied</strong>
    </div>
    <div class="warning-content">
      <p>You do not have permission to view work orders for the selected project.</p>
      <p>
        If you need access to this project, please
        <a href="/support">go to support page</a>.
      </p>
    </div>
  </mat-card>
</div>

@if (!loading && (isProjectAuthorized || isViewingDownloadedContent)) {
  <main>
    @if (hasStatusBanner()) {
      <div class="offline-fallback-banner" [ngClass]="getStatusBannerClass()">
        <mat-icon aria-hidden="true" [class.syncing-icon]="onlineSyncBannerState === 'syncing'">{{
          getStatusBannerIcon()
        }}</mat-icon>
        <div class="offline-fallback-copy">
          @if (onlineSyncBannerState === 'syncing') {
            <span>Back online. Trying to save your changes...</span>
          } @else if (onlineSyncBannerState === 'saved') {
            @if (recoveryHadPendingChanges) {
              <span>Changes saved successfully</span>
            } @else {
              <span>Back online</span>
            }
          } @else {
            @if (pendingChangeCount > 0) {
              <span>
                You are offline. {{ pendingChangeCount }} {{ pendingChangeCount === 1 ? 'change' : 'changes' }} on
                {{ pendingWorkOrderCount }} {{ pendingWorkOrderCount === 1 ? 'work-order' : 'work-orders' }} while
                offline.
              </span>
            } @else {
              <span>The application seems to be offline. Only downloaded work orders are available.</span>
            }
          }
        </div>
        @if (onlineSyncBannerState === 'saved') {
          <button
            mat-icon-button
            class="status-close-button"
            (click)="dismissSavedBanner()"
            aria-label="Close status banner"
          >
            <mat-icon>close</mat-icon>
          </button>
        } @else if (showOfflineFallbackBanner) {
          <button
            mat-stroked-button
            class="offline-retry-button"
            (click)="retryLoadWorkOrders()"
            [disabled]="isRetryingOnline || !isOnline"
          >
            @if (!isRetryingOnline) {
              Retry online
            } @else {
              Retrying...
            }
          </button>
        }
      </div>
    }
    @if (loadErrorMessage && !showOfflineFallbackBanner) {
      <div class="offline-fallback-banner status-offline">
        <mat-icon aria-hidden="true">error_outline</mat-icon>
        <div class="offline-fallback-copy">
          <span>{{ loadErrorMessage }}</span>
        </div>
      </div>
    }
    @if (!showSearchBar && (hasWorkOrders() || getWorkOrderService().searchText || areFiltersApplied())) {
      <div class="filter-bar">
        <div class="search-bar-minified">
          @if (getWorkOrderService().searchText) {
            <button mat-icon-button (click)="closeSearchBar()">
              <mat-icon>close</mat-icon>
            </button>
          }
          <input
            class="text-search-minified"
            [(ngModel)]="getWorkOrderService().searchText"
            placeholder="{{ 'workOrderList.searchLabel' | translate }}..."
            (click)="openSearchBar()"
          />
          <button (click)="openSearchBar()" class="button-search">
            <i class="material-icons">search</i>
          </button>
        </div>
        @if (hasWorkOrders()) {
          <div class="sort-options">
            <button class="sort-selection-button" (click)="openSortingOptionDialog()" data-ai-id="dashboard-sort">
              <span class="sort-selection-text">{{ getSelectedSortingOption().label | translate }}</span>
              <span class="sort-selection-icon"><i class="material-icons icon">arrow_drop_down</i></span>
            </button>
          </div>
        }
        <mat-form-field class="filter-bar site-filter-dropdown">
          <mat-label>Site...</mat-label>
          <mat-select multiple [(ngModel)]="selectedSites" (selectionChange)="changeSelectedSiteFilters()">
            @for (site of sites; track site) {
              <mat-option [value]="site">{{ site }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        <mat-form-field class="filter-bar task-filter-dropdown">
          <mat-label>Task No...</mat-label>
          <mat-select multiple [(ngModel)]="selectedTaskNos" (selectionChange)="changeSelectedTaskFilters()">
            @for (taskNo of taskNos; track taskNo) {
              <mat-option [value]="taskNo">{{ taskNo }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        @if (areFiltersApplied()) {
          <button
            class="button-search-reset"
            mat-icon-button
            (click)="resetFilters()"
            data-ai-id="dashboard-reset-filters"
          >
            <img src="assets/icons/clear-filter.svg" alt="Clear Filters" width="30" height="30" />
          </button>
        }
      </div>
    }
    @if (showSearchBar) {
      <div class="search-bar">
        <input
          class="text-filter"
          [value]="searchText$ | async"
          (keyup)="onKeyUp($event.target.value)"
          placeholder="{{ 'workOrderList.searchLabel' | translate }}..."
          appAutoFocus
        />
        <button class="close-button" (click)="closeSearchBar()" mat-stroked-button color="primary">
          <span> {{ 'components.searchBar.cancel' | translate }} </span>
        </button>
        <button class="ok-button" (click)="acceptSearch()" mat-flat-button color="primary">
          <span> {{ 'components.searchBar.ok' | translate }} </span>
        </button>
      </div>
    }
    @if (getFilters().length > 0) {
      <div class="filter-options">
        <nav mat-tab-nav-bar>
          @for (filter of getFilters(); track filter) {
            <a
              mat-tab-link
              color="primary"
              [id]="filter.id"
              [active]="filter == getSelectedFilter()"
              (click)="setSelectedFilter(filter)"
              data-ai-id="dashboard-filter"
              [attr.data-ai-filter]="filter.id"
              >{{ filter.label | translate }} ({{ getAllWorkOrders().filter(filter.filter).length }})
            </a>
          }
        </nav>
      </div>
    }
    @if (isDownloadedTabSelected()) {
      <div class="download-actions">
        <button
          mat-stroked-button
          color="primary"
          (click)="syncAllDownloaded()"
          [disabled]="isSyncingAllDownloaded"
          data-ai-id="dashboard-sync-all"
        >
          @if (!isSyncingAllDownloaded) {
            Sync all
          } @else {
            Syncing...
          }
        </button>
        <button
          mat-stroked-button
          color="warn"
          (click)="clearOfflineData()"
          [disabled]="isClearingOfflineData || showOfflineFallbackBanner"
          data-ai-id="dashboard-clear-offline"
        >
          @if (!isClearingOfflineData) {
            Clear offline
          } @else {
            Clearing...
          }
        </button>
      </div>
    }
    @if (hasDisclaimer()) {
      <div class="disclaimer">
        <div class="update-notification"></div>
        {{ 'disclaimers.updateDownloaded' | translate }}
      </div>
    }
    @if (hasWorkOrders()) {
      <div class="work-orders-grid">
        @for (workOrder of getWorkOrders(); track workOrder) {
          <div class="work-order-tile">
            <app-work-order-card
              [workOrder]="workOrder"
              [isOnline]="isOnline"
              [isOfflineFallback]="showOfflineFallbackBanner"
              [showProjectTitle]="hasMultipleActiveProjects"
              [projectTitle]="getProjectTitle(workOrder.projectId)"
              [hasNotification]="hasNotification(workOrder.id)"
              [hasNewNotification]="isNewNotification(workOrder.id)"
              [refreshPerformed]="refreshPerformed(workOrder.id)"
              (downloaded)="onWorkOrderDownloadChange($event, true)"
              (downloadRemoved)="onWorkOrderDownloadChange($event, false)"
              (toggleFavourite)="reorder()"
            ></app-work-order-card>
          </div>
        }
      </div>
    }
    @if (!hasWorkOrders()) {
      <app-empty-list info="{{ 'workOrderList.empty.info' | translate }}"></app-empty-list>
    }
  </main>
}
